import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ReadinessChecklistItem } from '@/components/organizations/ReadinessChecklistItem'
import { usePathHelper } from '@/hooks/usePathHelper'
import { ConnectAccountStatus } from '@/lib/enums/ConnectAccountStatus'
import { useOrgOnboardingStatus } from '@/lib/services/organizations/organizations.query'
import { stripeHubLink, stripeOnboardingLink } from '@/lib/stripe-financial-routes'
import { useParams } from '@tanstack/react-router'
import { Typography } from '@/components/ui/typography'
import { useAbility } from '@/lib/casl'

function AccountStatus() {
  const { orgId } = useParams({ strict: false })
  const { data: onboarding } = useOrgOnboardingStatus(orgId)
  const path = usePathHelper()
  const ability = useAbility()
  const canEditStripeAccount = ability.can('edit', 'stripe-account')
  const stripeSetupLink =
    canEditStripeAccount &&
    ![ConnectAccountStatus.NotCreated.Value, ConnectAccountStatus.Incomplete.Value].includes(
      onboarding?.stripe_status
    )
      ? stripeHubLink(orgId)
      : canEditStripeAccount
        ? stripeOnboardingLink(orgId)
        : undefined

  if (onboarding?.is_complete) {
    return <></>
  }

  const accountChecks = [
    {
      success: onboarding?.stripe_status === ConnectAccountStatus.Verified.Value,
      title: 'Stripe account setup',
      description: "You can't get paid right now",
      link: stripeSetupLink,
      canView: canEditStripeAccount,
    },
    {
      success: onboarding?.locations > 0,
      title: 'Active location',
      description: 'At least one location must be active',
      link: path.locations.index().link,
      canView: ability.can('list', 'locations'),
    },
    {
      success: onboarding?.default_location > 0,
      title: 'Default location',
      description: 'Choose the primary location for your website',
      link: path.locations.index().link,
      canView: ability.can('list', 'locations'),
    },
    {
      success: onboarding?.domains > 0,
      title: 'Verified domain',
      description: "Customers can't get to your website",
      link: path.domains.index().link,
      canView: ability.can('list', 'domains'),
    },
    {
      success: onboarding?.notification_recipients > 0,
      title: 'Enabled notifications',
      description: "You won't be notified about new orders",
      link: path.notifications.index().link,
      canView: ability.can('list', 'notification-recipients'),
    },
    {
      success: onboarding?.products > 0,
      title: 'Active product',
      description: 'You have nothing to sell',
      link: path.products.index().link,
      canView: ability.can('list', 'products'),
    },
    {
      success: onboarding?.collections > 0,
      title: 'Product collections',
      description: 'Collections help customers browse your website',
      link: path.collections.index().link,
      canView: ability.can('list', 'collections'),
    },
  ]

  return (
    <Card className="max-w-4xl">
      <CardHeader>
        <CardTitle>
          <Typography variant="h3">Account Status</Typography>
        </CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {accountChecks.map(({ success, title, description, link, canView }) => (
          <ReadinessChecklistItem
            key={title}
            description={description}
            href={canView ? link : undefined}
            isReady={success}
            title={title}
          />
        ))}
      </CardContent>
    </Card>
  )
}

export default AccountStatus
